import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "../context/ThemeContext";

// Esri ArcGIS Online basemaps (no API key, rate-limited but fine for a hotel site).
// Light: colorful street map. Dark: satellite imagery with a transportation/labels overlay
// for readability — evocative night-view feel over Turin's Po river + hills.
const TILES = {
  dark: {
    base: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ref: "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
    baseMaxZoom: 19,
  },
  light: {
    base: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    ref: null,
    baseMaxZoom: 19,
  },
};
const ESRI_ATTRIB = 'Tiles &copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, OpenStreetMap contributors';

const goldIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:36px;height:46px;transform:translate(-50%,-100%);">
      <div style="position:absolute;inset:0;filter:drop-shadow(0 6px 10px rgba(0,0,0,0.6));">
        <svg width="36" height="46" viewBox="0 0 36 46" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 0C8.06 0 0 7.94 0 17.73 0 30.64 15.6 44.4 17.08 45.59a1.5 1.5 0 0 0 1.84 0C20.4 44.4 36 30.64 36 17.73 36 7.94 27.94 0 18 0z" fill="#C5A059"/>
          <circle cx="18" cy="17" r="6" fill="#09090b"/>
        </svg>
      </div>
    </div>
  `,
  iconSize: [36, 46],
  iconAnchor: [18, 46],
});

const originIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:28px;height:28px;transform:translate(-50%,-50%);">
      <div style="position:absolute;inset:0;border-radius:50%;background:#FFFFF0;border:3px solid #09090b;box-shadow:0 3px 10px rgba(0,0,0,0.7),0 0 0 3px #C5A059,0 0 0 6px rgba(197,160,89,0.35);"></div>
    </div>
  `,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const LABELS_PANE = "esriLabels";

export default function EsriMap({ lat, lng, label = "", origin = null, routePath = null }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const baseRef = useRef(null);
  const refRefLayer = useRef(null);
  const originMarkerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const routeHaloRef = useRef(null);
  const { theme } = useTheme();

  // Init map once
  useEffect(() => {
    if (!ref.current || mapRef.current) return;

    const map = L.map(ref.current, {
      center: [lat, lng],
      zoom: 16,
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    // Pane custom per l'overlay etichette Esri: z-index 350 sta
    // fra tilePane (200) e overlayPane (400). Cosi' le etichette
    // dei trasporti coprono il basemap ma NON la polyline di
    // routing o i marker (che stanno nell'overlayPane di default).
    if (!map.getPane(LABELS_PANE)) {
      map.createPane(LABELS_PANE);
      map.getPane(LABELS_PANE).style.zIndex = 350;
      map.getPane(LABELS_PANE).style.pointerEvents = "none";
    }

    const tiles = TILES[theme] || TILES.dark;
    baseRef.current = L.tileLayer(tiles.base, { attribution: ESRI_ATTRIB, maxZoom: tiles.baseMaxZoom }).addTo(map);
    if (tiles.ref) {
      refRefLayer.current = L.tileLayer(tiles.ref, { pane: LABELS_PANE, maxZoom: tiles.baseMaxZoom }).addTo(map);
    }

    L.marker([lat, lng], { icon: goldIcon, title: label })
      .addTo(map)
      .bindPopup(
        `<div style="font-family:Outfit,sans-serif;color:#09090b;font-size:13px;line-height:1.5;">
          <strong style="font-family:'Cormorant Garamond',serif;font-size:15px;display:block;margin-bottom:2px;">Hotel Alpi Resort</strong>
          ${label}
        </div>`,
        { closeButton: false, offset: [0, -40] }
      );

    setTimeout(() => map.invalidateSize(), 200);

    return () => {
      map.remove();
      mapRef.current = null;
      baseRef.current = null;
      refRefLayer.current = null;
      originMarkerRef.current = null;
      routeLayerRef.current = null;
      routeHaloRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, label]);

  // Swap basemap when theme changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const tiles = TILES[theme] || TILES.dark;
    if (baseRef.current) { map.removeLayer(baseRef.current); baseRef.current = null; }
    if (refRefLayer.current) { map.removeLayer(refRefLayer.current); refRefLayer.current = null; }
    baseRef.current = L.tileLayer(tiles.base, { attribution: ESRI_ATTRIB, maxZoom: tiles.baseMaxZoom }).addTo(map);
    if (tiles.ref) {
      refRefLayer.current = L.tileLayer(tiles.ref, { pane: LABELS_PANE, maxZoom: tiles.baseMaxZoom }).addTo(map);
    }
  }, [theme]);

  // Draw / clear origin + route when inputs change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (routeHaloRef.current) { map.removeLayer(routeHaloRef.current); routeHaloRef.current = null; }
    if (routeLayerRef.current) { map.removeLayer(routeLayerRef.current); routeLayerRef.current = null; }
    if (originMarkerRef.current) { map.removeLayer(originMarkerRef.current); originMarkerRef.current = null; }

    if (!origin && !routePath) {
      map.setView([lat, lng], 16);
      return;
    }

    if (origin) {
      originMarkerRef.current = L.marker([origin.lat, origin.lng], { icon: originIcon }).addTo(map);
    }
    if (routePath && routePath.length > 1) {
      // Route polyline: azzurro brillante (convenzione nav) con halo
      // scuro sotto. L'oro del brand e' riservato ai pin; sul basemap
      // satellitare (tetti rossi / verde parchi / fiume) l'oro si
      // confondeva a tratti, il blu si stacca su ogni texture.
      routeHaloRef.current = L.polyline(routePath, {
        color: "#09090b",
        weight: 8,
        opacity: 0.7,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(map);
      routeLayerRef.current = L.polyline(routePath, {
        color: "#38BDF8",
        weight: 5,
        opacity: 1,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(map);
      map.fitBounds(routeLayerRef.current.getBounds(), { padding: [40, 40] });
    } else if (origin) {
      map.fitBounds(L.latLngBounds([[origin.lat, origin.lng], [lat, lng]]), { padding: [40, 40] });
    }
  }, [origin, routePath, lat, lng]);

  return (
    <div
      ref={ref}
      data-testid="esri-map"
      className="w-full h-full"
      style={{ background: "var(--surface)" }}
    />
  );
}
